import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faBan, faCheck, faPhone, faCopy } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../../AuthContext';
import './AdminUsers.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

const statusColors = {
  active: 'status-completed',
  suspended: 'status-cancelled',
  inactive: 'status-pending',
};

function PhoneCell({ phone, name, username }) {
  const [copied, setCopied] = useState(false);

  if (!phone) return <span className="no-phone">—</span>;

  const rawPhone = phone.trim();
  const cleanDigits = rawPhone.replace(/\D/g, '');
  const waUrl = `https://wa.me/${cleanDigits}?text=Hello%20${encodeURIComponent(name || username || 'Customer')}`;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(rawPhone);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="admin-phone-cell">
      <span className="phone-number-text">{rawPhone}</span>
      <div className="phone-actions">
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="phone-action-btn wa-btn"
          title="Chat on WhatsApp"
        >
          <FontAwesomeIcon icon={faWhatsapp} />
        </a>
        <a
          href={`tel:${cleanDigits}`}
          className="phone-action-btn call-btn"
          title="Call phone"
        >
          <FontAwesomeIcon icon={faPhone} />
        </a>
        <button
          type="button"
          className={`phone-action-btn copy-btn ${copied ? 'copied' : ''}`}
          onClick={handleCopy}
          title={copied ? 'Copied!' : 'Copy phone'}
        >
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
          {copied && <span className="phone-copied-tooltip">Copied!</span>}
        </button>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchUsers = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);

    fetch(`${API}/admin/users?${params}`, { headers })
      .then((r) => r.json())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  useEffect(() => { fetchUsers(); }, [token, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    await fetch(`${API}/admin/users/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status: newStatus }),
    });
    fetchUsers();
  };

  return (
    <div className="admin-users">
      <div className="search-bar admin-search">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          type="text"
          className="search-input"
          placeholder="Search by name, username, email or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="admin-table-card">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Username</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Balance</th>
              <th>Orders</th>
              <th>Spent</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className="id-cell">{u.id}</td>
                <td className="name-cell">{u.name}</td>
                <td>@{u.username}</td>
                <td className="phone-td">
                  <PhoneCell phone={u.phone} name={u.name} username={u.username} />
                </td>
                <td>{u.email}</td>
                <td className="balance-cell">TSHS {Number(u.balance || 0).toLocaleString()}</td>
                <td>{u.orders_count ?? '—'}</td>
                <td>TSHS {Number(u.total_spent || 0).toLocaleString()}</td>
                <td>
                  <span className={`status-badge ${statusColors[u.status] || ''}`}>{u.status}</span>
                </td>
                <td className="date-cell">{new Date(u.created_at).toLocaleDateString()}</td>
                <td>
                  <button
                    className={`action-btn ${u.status === 'active' ? 'suspend' : 'activate'}`}
                    onClick={() => toggleStatus(u.id, u.status)}
                    title={u.status === 'active' ? 'Suspend user' : 'Activate user'}
                  >
                    <FontAwesomeIcon icon={u.status === 'active' ? faBan : faCheck} />
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={11} className="empty-cell">No users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

